import type { CalibrePreviewDto } from "@shelflist/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, FolderOpen } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Field, fieldA11y } from "@/components/field";
import { Page } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useFormat } from "@/i18n/format";
import { ApiError, api, call } from "@/lib/api";
import { useErrorText } from "@/lib/errors";
import { calibreLibrariesQuery, jobKeys } from "@/lib/jobs";

export const Route = createFileRoute("/_app/admin/import/calibre")({
  component: CalibreImportPage,
});

function CalibreImportPage() {
  const { t } = useTranslation("calibreImport");
  const format = useFormat();
  const errorText = useErrorText();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const libraries = useQuery(calibreLibrariesQuery);
  const [path, setPath] = useState("");
  const [importRatings, setImportRatings] = useState(false);
  const [refreshMetadata, setRefreshMetadata] = useState(false);

  const preview = useMutation({
    mutationFn: (libraryPath: string) =>
      call(api.admin.calibre.preview.$post({ json: { path: libraryPath } })),
  });
  const start = useMutation({
    mutationFn: () =>
      call(
        api.admin.calibre.imports.$post({
          json: { path: previewed?.path ?? path, importRatings, refreshMetadata },
        }),
      ),
    onSuccess: async ({ jobId }) => {
      await queryClient.invalidateQueries({ queryKey: jobKeys.all });
      await navigate({ to: "/admin/jobs/$jobId", params: { jobId } });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === "calibre_import_in_progress") {
        void queryClient.invalidateQueries({ queryKey: jobKeys.libraries });
      }
    },
  });
  const previewed = preview.data?.preview as CalibrePreviewDto | undefined;
  const activeJobId = libraries.data?.activeJobId ?? null;
  const root = libraries.data?.root ?? "/import";

  const check = (libraryPath: string) => {
    setPath(libraryPath);
    start.reset();
    preview.mutate(libraryPath);
  };

  const errorOf = (error: unknown): string | undefined => {
    if (!(error instanceof ApiError)) return error ? errorText(undefined) : undefined;
    const missing = error.details.missing;
    return error.code === "calibre_db_unsupported_schema" && missing
      ? `${errorText(error.code)} ${t("errors.missing", { missing })}`
      : errorText(error.code);
  };

  return (
    <Page title={t("title")} description={t("description")}>
      {activeJobId ? (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3"
        >
          <span>{t("active")}</span>
          <Button asChild variant="outline" size="sm">
            <Link to="/admin/jobs/$jobId" params={{ jobId: activeJobId }}>
              {t("viewJob")}
            </Link>
          </Button>
        </div>
      ) : null}

      <div className="grid gap-6">
        <section aria-labelledby="step1" className="grid gap-4">
          <h2 id="step1" className="text-lg font-semibold">
            {t("step1")}
          </h2>
          <p className="text-muted-foreground text-sm">{t("root", { root })}</p>

          {libraries.data && libraries.data.libraries.length > 0 ? (
            <ul className="grid gap-2" aria-label={t("detected")}>
              {libraries.data.libraries.map((library) => (
                <li
                  key={library.path}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3"
                >
                  <div className="grid gap-0.5">
                    <span className="flex items-center gap-2 font-medium">
                      <FolderOpen className="size-4" aria-hidden />
                      {library.path}
                    </span>
                    <span className="text-muted-foreground text-sm">
                      {library.bookCount === null
                        ? t("bookCountUnknown")
                        : t("bookCount", { count: library.bookCount })}
                      {" · "}
                      {t("modified", { date: format.date(library.modifiedAt) })}
                    </span>
                  </div>
                  <Button
                    variant={path === library.path ? "default" : "outline"}
                    size="sm"
                    disabled={preview.isPending}
                    onClick={() => check(library.path)}
                  >
                    {t("check")}
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}

          {libraries.data?.hint ? (
            <div className="grid gap-2 rounded-lg border border-dashed px-4 py-5">
              <h3 className="font-medium">{t("noLibraries.title")}</h3>
              <p className="text-muted-foreground text-sm">{t("noLibraries.description")}</p>
              <pre className="bg-muted overflow-x-auto rounded-md px-3 py-2 text-sm">
                <code>{t("noLibraries.example")}</code>
              </pre>
              <p className="text-muted-foreground text-sm">{t("noLibraries.note")}</p>
              <Button
                variant="outline"
                size="sm"
                className="justify-self-start"
                onClick={() => void libraries.refetch()}
              >
                {t("refresh")}
              </Button>
            </div>
          ) : null}

          <form
            className="grid gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (path.trim()) check(path.trim());
            }}
          >
            <Field
              id="library-path"
              label={t("pathLabel")}
              hint={t("pathHint")}
              error={preview.error instanceof ApiError ? "_" : undefined}
            >
              <Input
                {...fieldA11y("library-path")}
                value={path}
                placeholder={t("pathPlaceholder", { root })}
                onChange={(event) => {
                  setPath(event.target.value);
                  preview.reset();
                }}
                autoComplete="off"
                spellCheck={false}
              />
            </Field>
            <Button
              type="submit"
              variant="outline"
              className="justify-self-start"
              disabled={!path.trim() || preview.isPending}
            >
              {preview.isPending ? t("checking") : t("check")}
            </Button>
          </form>
          {preview.error ? (
            <p role="alert" className="text-destructive text-sm font-medium">
              {errorOf(preview.error)}
            </p>
          ) : null}
        </section>

        {previewed ? <PreviewSection preview={previewed} /> : null}

        {previewed ? (
          <section aria-labelledby="step3" className="grid gap-4">
            <h2 id="step3" className="text-lg font-semibold">
              {t("step3")}
            </h2>
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 size-4"
                checked={importRatings}
                onChange={(event) => setImportRatings(event.target.checked)}
              />
              <span className="grid gap-0.5">
                <span className="font-medium">{t("options.importRatings")}</span>
                <span className="text-muted-foreground text-sm">
                  {t("options.importRatingsHint")}
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 size-4"
                checked={refreshMetadata}
                onChange={(event) => setRefreshMetadata(event.target.checked)}
              />
              <span className="grid gap-0.5">
                <span className="font-medium">{t("options.refreshMetadata")}</span>
                <span className="text-muted-foreground text-sm">
                  {t("options.refreshMetadataHint")}
                </span>
              </span>
            </label>
            {refreshMetadata ? (
              <p role="alert" className="flex items-start gap-2 text-sm font-medium">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
                {t("options.refreshMetadataWarning", {
                  count: previewed.alreadyImported,
                })}
              </p>
            ) : null}
            {previewed.insufficientSpace ? (
              <p
                role="alert"
                className="text-destructive flex items-start gap-2 text-sm font-medium"
              >
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
                {t("preview.insufficientSpace")}
              </p>
            ) : null}
            {start.error ? (
              <p role="alert" className="text-destructive text-sm font-medium">
                {errorOf(start.error)}
              </p>
            ) : null}
            {activeJobId ? (
              <Button asChild className="justify-self-start">
                <Link to="/admin/jobs/$jobId" params={{ jobId: activeJobId }}>
                  {t("viewJob")}
                </Link>
              </Button>
            ) : (
              <Button
                className="justify-self-start"
                disabled={start.isPending || previewed.totalBooks === 0}
                onClick={() => start.mutate()}
              >
                {start.isPending ? t("options.starting") : t("options.start")}
              </Button>
            )}
          </section>
        ) : null}
      </div>
    </Page>
  );
}

function PreviewSection({ preview }: { preview: CalibrePreviewDto }) {
  const { t } = useTranslation("calibreImport");
  const format = useFormat();
  const stats: [string, string][] = [
    ["totalBooks", format.number(preview.totalBooks)],
    ["alreadyImported", format.number(preview.alreadyImported)],
    ["toConsider", format.number(preview.toConsider)],
    ["booksWithoutFiles", format.number(preview.booksWithoutFiles)],
    ["authors", format.number(preview.authors)],
    ["series", format.number(preview.series)],
    ["tags", format.number(preview.tags)],
    ["publishers", format.number(preview.publishers)],
    ["languages", format.number(preview.languages)],
    ["ratedBooks", format.number(preview.ratedBooks)],
    ["totalBytes", format.bytes(preview.totalBytes)],
    ["freeBytes", format.bytes(preview.freeBytes)],
  ];
  const formatList = (entries: Record<string, number>, upper: boolean): string => {
    const items = Object.entries(entries).sort(([a], [b]) => a.localeCompare(b));
    return items.length === 0
      ? t("preview.none")
      : items
          .map(
            ([name, count]) =>
              `${upper ? name.toUpperCase() : name.toUpperCase()}: ${format.number(count)}`,
          )
          .join(", ");
  };
  return (
    <section aria-labelledby="step2" className="grid gap-4">
      <h2 id="step2" className="text-lg font-semibold">
        {t("step2")}
      </h2>
      <p className="text-muted-foreground text-sm">{t("preview.title", { path: preview.path })}</p>
      {preview.totalBooks === 0 ? (
        <p className="text-muted-foreground">{t("preview.empty")}</p>
      ) : null}
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {stats.map(([key, value]) => (
          <div key={key} className="rounded-lg border px-3 py-2">
            <dt className="text-muted-foreground text-xs">{t(`preview.${key as "totalBooks"}`)}</dt>
            <dd className="text-xl font-semibold">{value}</dd>
          </div>
        ))}
      </dl>
      <dl className="grid gap-2 text-sm">
        <div>
          <dt className="text-muted-foreground inline">{t("preview.formats")}: </dt>
          <dd className="inline">{formatList(preview.formats, true)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground inline">{t("preview.skippedFormats")}: </dt>
          <dd className="inline">{formatList(preview.skippedFormats, true)}</dd>
        </div>
      </dl>
      {preview.missingFiles > 0 ? (
        <p className="flex items-start gap-2 text-sm">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
          {t("preview.missingWarning", { count: preview.missingFiles })}
        </p>
      ) : null}
      <p className="text-muted-foreground text-sm">{t("preview.hashNote")}</p>
    </section>
  );
}
